import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import "../CareerCenters/CareerHeader/CareerHeader.css";
import "./Alumni.css";
import Seo from "../../seo/Seo";

function FadeIn({ children, delay = 0, className }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 14, delay }}
    >
      {children}
    </motion.div>
  );
}

const DEFAULT_PALETTE = { tubes: ["#22c55e", "#eab308", "#2dd4bf"], lights: ["#16a34a", "#ca8a04", "#0d9488", "#4ade80"] };
const CALENDLY_BASE = "https://calendly.com/elliotcarlson-rain/rain-meeting-30-mins";

const AlumniPage = () => {
  const canvasRef = useRef(null);
  const calendlyRef = useRef(null);
  const [formData, setFormData] = useState({ name: "", school: "", email: "" });
  const [calendlyUrl, setCalendlyUrl] = useState(null);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    let animFrameId = null;
    let t = -Math.PI / 2;

    const init = async () => {
      if (!canvasRef.current) return;
      try {
        const capturedHandlers = [];
        const intercept = (target) => {
          const orig = target.addEventListener.bind(target);
          target.addEventListener = (type, fn, opts) => {
            if (type === "mousemove" || type === "pointermove") {
              capturedHandlers.push(fn);
              return;
            }
            orig(type, fn, opts);
          };
          return orig;
        };
        const origWindow   = intercept(window);
        const origDocument = intercept(document);
        const origCanvas   = intercept(canvasRef.current);

        const mod = await import(
          /* webpackIgnore: true */
          "https://cdn.jsdelivr.net/npm/threejs-components@0.0.19/build/cursors/tubes1.min.js"
        );
        if (!mounted) return;

        window.addEventListener            = origWindow;
        document.addEventListener          = origDocument;
        canvasRef.current.addEventListener = origCanvas;

        const TubesCursor = mod.default;
        TubesCursor(canvasRef.current, {
          tubes: {
            colors: DEFAULT_PALETTE.tubes,
            lights: { intensity: 200, colors: DEFAULT_PALETTE.lights },
          },
        });

        const drive = () => {
          if (!mounted || !canvasRef.current) return;
          const rect = canvasRef.current.getBoundingClientRect();
          const cx = rect.left + rect.width  / 2;
          const cy = rect.top  + rect.height / 2;
          const rx = rect.width  * 0.32;
          const ry = rect.height * 0.28;

          const x = cx + rx * Math.sin(t);
          const y = cy + ry * Math.sin(2 * t);

          const synth = new MouseEvent("mousemove", { clientX: x, clientY: y, bubbles: false });
          capturedHandlers.forEach((fn) => fn(synth));

          t += 0.006;
          animFrameId = requestAnimationFrame(drive);
        };
        animFrameId = requestAnimationFrame(drive);

      } catch (err) {
        console.error("Tubes failed to load:", err);
      }
    };

    init();
    return () => {
      mounted = false;
      if (animFrameId) cancelAnimationFrame(animFrameId);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!calendlyUrl || !calendlyRef.current) return;
    const tryInit = () => {
      if (window.Calendly) {
        window.Calendly.initInlineWidget({
          url: calendlyUrl,
          parentElement: calendlyRef.current,
        });
      } else {
        setTimeout(tryInit, 100);
      }
    };
    tryInit();
  }, [calendlyUrl]);

  const handleShowCalendly = (e) => {
    e.preventDefault();
    const enc = encodeURIComponent;
    const url = `${CALENDLY_BASE}?hide_event_type_details=1&hide_gdpr_banner=1` +
      `&name=${enc(formData.name)}` +
      `&email=${enc(formData.email)}` +
      `&a1=${enc(formData.school)}` +
      `&a2=${enc("Alumni Engagement Inquiry")}`;
    setCalendlyUrl(url);
  };

  return (
    <div className="alumni-page">
    <Seo path="/alumni" />
    <section className="cc-hero" id="alumni-hero">
      <div className="cc-hero-left">
        <FadeIn delay={0}>
          <p className="cc-hero-eyebrow">Now in development!</p>
        </FadeIn>
        <FadeIn delay={0.1}>
          <h1 className="cc-hero-heading">
            A Smarter Way to Keep Your <span style={{ color: "rgb(22, 163, 74)" }}>Alumni Engaged </span>
          </h1>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="cc-hero-description">
            RAIN is building an alumni engagement platform that goes beyond directories and donation forms, giving your alumni office the tools to drive mentorship, giving, and lifelong connection.
          </p>
        </FadeIn>
      </div>

      <FadeIn delay={0.3} className="cc-hero-right-fadein">
        <div className="cc-hero-right">
          <div className="cc-hero-module">
            <canvas ref={canvasRef} className="cc-hero-canvas" />

            {calendlyUrl ? (
              <div className="cc-hero-form-card cc-hero-calendly-card">
                <button className="cc-hero-back-btn" onClick={() => setCalendlyUrl(null)}>
                  ← Back
                </button>
                <div ref={calendlyRef} style={{ width: "100%", height: "460px" }} />
              </div>
            ) : (
              <div className="cc-hero-form-card">
                <h2 className="cc-hero-form-title">Let's Chat</h2>
                <form className="cc-hero-form" onSubmit={handleShowCalendly}>
                  <div className="cc-hf-row">
                    <div className="cc-hf-group">
                      <label>Full Name</label>
                      <input type="text" name="name" placeholder="Full Name"
                        value={formData.name} onChange={handleChange} required />
                    </div>
                    <div className="cc-hf-group">
                      <label>Institution</label>
                      <input type="text" name="school" placeholder="Your School"
                        value={formData.school} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="cc-hf-group">
                    <label>Work Email</label>
                    <input type="email" name="email" placeholder="you@university.edu"
                      value={formData.email} onChange={handleChange} required />
                  </div>
                  <button type="submit" className="cc-hf-submit">See Available Times</button>
                  <p className="cc-hf-footer">
                    You can also email us at{" "}
                    <a href="mailto:adminrain@raincareers.com">adminrain@raincareers.com</a>
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </FadeIn>
    </section>
    </div>
  );
};

export default AlumniPage;
